import { Node } from 'reactflow';
import {
  OperationalModel,
  OperationalStatus,
  SubLayersWithoutValidStatus,
  SubLayersWithScopeFilterOnly,
  SubLayerTypes,
} from '@constants/canvas/layers';
import {
  CiaFilters,
  ProjectFiltersContextProps,
} from '@context/ProjectFilters/types';
import { NodeDTO } from '@store/services/nodes/types';
import { isCriteriaFieldsMatch } from '@utils/canvasHelpers';

export type ModelFilters = Pick<
  ProjectFiltersContextProps,
  | 'validationStatus'
  | 'reviewStatus'
  | 'operationalStatuses'
  | 'operatingModelStatuses'
  | 'partners'
  | 'ciaFilters'
>;

type FilterableNodeDto = Partial<
  Pick<
    NodeDTO,
    'valid_status' | 'review_required' | 'partners' | 'cia' | 'is_reported'
  >
> & {
  meta_data?: {
    operational_status?: OperationalStatus;
    operational_model?: OperationalModel;
  };
};

const hasCiaSelection = (ciaFilters: CiaFilters) =>
  Object.values(ciaFilters).some((fields) =>
    Object.values(fields).some((answers) => answers?.length),
  );

const hasActiveModelFilter = ({
  validationStatus,
  reviewStatus,
  operationalStatuses,
  operatingModelStatuses,
  partners,
  ciaFilters,
}: ModelFilters) =>
  Boolean(
    validationStatus.length ||
      reviewStatus.length ||
      operationalStatuses.length ||
      operatingModelStatuses.length ||
      partners.length ||
      hasCiaSelection(ciaFilters),
  );

export const matchesModelFiltersDto = (
  dto: FilterableNodeDto | undefined,
  subLayer: SubLayerTypes | undefined,
  filters: ModelFilters,
) => {
  const {
    validationStatus,
    reviewStatus,
    operationalStatuses,
    operatingModelStatuses,
    partners,
    ciaFilters,
  } = filters;

  if (subLayer && SubLayersWithScopeFilterOnly.includes(subLayer)) {
    return !hasActiveModelFilter(filters);
  }

  if (reviewStatus.length && !dto?.review_required) return false;

  if (validationStatus.length) {
    const hasNoValidStatus =
      !!subLayer && SubLayersWithoutValidStatus.includes(subLayer);

    const validStatus = dto?.valid_status;

    if (
      hasNoValidStatus ||
      !validStatus ||
      !validationStatus.includes(validStatus)
    ) {
      return false;
    }
  }

  const operationalStatus = dto?.meta_data?.operational_status;

  if (
    operationalStatuses.length &&
    (!operationalStatus || !operationalStatuses.includes(operationalStatus))
  ) {
    return false;
  }

  const operatingModel = dto?.meta_data?.operational_model;

  if (
    operatingModelStatuses.length &&
    (!operatingModel || !operatingModelStatuses.includes(operatingModel))
  ) {
    return false;
  }

  if (
    partners.length &&
    !partners.some((partner) => dto?.partners?.includes(partner))
  ) {
    return false;
  }

  if (hasCiaSelection(ciaFilters)) {
    const isAllMatch =
      isCriteriaFieldsMatch(
        ciaFilters.assurance_level,
        dto?.cia?.assurance_level?.criterias,
      ) &&
      isCriteriaFieldsMatch(
        ciaFilters.coverage,
        dto?.cia?.coverage?.criterias,
      ) &&
      isCriteriaFieldsMatch(
        ciaFilters.applicability,
        dto?.cia?.applicability?.criterias,
      ) &&
      dto?.is_reported !== false;

    if (!isAllMatch) return false;
  }

  return true;
};

export const matchesModelFilters = (node: Node, filters: ModelFilters) =>
  matchesModelFiltersDto(
    node.data?.dto,
    node.parentNode as SubLayerTypes,
    filters,
  );
